// Team Partners Mockup - SOTA Clinical Collaboration (GitHub-inspired)
// Concept: GitHub-like collaboration for clinical workflows

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Chip,
  Avatar,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Tooltip,
  Badge,
  Stack,
  Alert,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  Link as LinkIcon,
  Share as ShareIcon,
  Security as SecurityIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  LocalHospital as HospitalIcon,
  LocalHospital as LocalHospitalIcon,
  Science as ScienceIcon,
  LocalPharmacy as PharmacyIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Comment as CommentIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  VpnKey as KeyIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function TeamPartnersMockup() {
  const [activeTab, setActiveTab] = useState(0);
  const [shareMode, setShareMode] = useState<'image' | 'segmentation' | 'full'>('full');
  const [shareAccess, setShareAccess] = useState<'view' | 'comment' | 'edit'>('comment');
  const [requireAuth, setRequireAuth] = useState(true);
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const generateShareLink = () => {
    const shareId = `SH${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
    const link = `https://skin.noetiv.com/shared/${shareId}`;
    setGeneratedLink(link);
  };

  // Mock data
  const mockPartners = [
    {
      id: 1,
      name: 'Dr. Georgios Savvidis',
      email: 'g.savvidis@oncology-partners.com',
      organization: 'Oncology Partners',
      type: 'oncologist',
      role: 'External Reviewer',
      cases: 12,
      lastActive: '2 hours ago',
      status: 'active',
      avatar: 'GS',
    },
    {
      id: 2,
      name: 'PathLab Diagnostics',
      email: 'contact@pathlab.com',
      organization: 'PathLab Diagnostics',
      type: 'lab',
      role: 'Lab Partner',
      cases: 45,
      lastActive: '1 day ago',
      status: 'active',
      avatar: 'PL',
    },
    {
      id: 3,
      name: 'Dr. Michael Chen',
      email: 'm.chen@dermatology-group.com',
      organization: 'Dermatology Group',
      type: 'dermatologist',
      role: 'Second Opinion',
      cases: 8,
      lastActive: '3 days ago',
      status: 'active',
      avatar: 'MC',
    },
  ];

  const mockSharedCases = [
    {
      id: 'CASE-2024-0892',
      patient: 'Patient #4521',
      sharedWith: 'Dr. Sarah Johnson',
      shareDate: '2024-02-15',
      accessLevel: 'comment',
      status: 'reviewed',
      expiresIn: '14 days',
    },
    {
      id: 'CASE-2024-0887',
      patient: 'Patient #4518',
      sharedWith: 'PathLab Diagnostics',
      shareDate: '2024-02-12',
      accessLevel: 'view',
      status: 'pending',
      expiresIn: '21 days',
    },
  ];

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'oncologist':
        return <HospitalIcon />;
      case 'lab':
        return <ScienceIcon />;
      case 'dermatologist':
        return <LocalHospitalIcon />;
      case 'pharmacy':
        return <PharmacyIcon />;
      default:
        return <PersonIcon />;
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <GroupIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              Team Partners
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Clinical collaboration
            </Typography>
          </Box>
        </Box>

        <Alert severity="info" icon={<HistoryIcon />} sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Feature in Development
          </Typography>
          <Typography variant="caption">
            Collaborate easy: Org = Clinic, Teams = Nurses/Doctors, Partners = External Collaborators, Cases = Repos
          </Typography>
        </Alert>
      </Box>

      {/* Main Navigation */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: alpha('#000', 0.02) }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                minHeight: 64,
              },
            }}
          >
            <Tab
              icon={<PersonAddIcon />}
              iconPosition="start"
              label="Partner Members"
              sx={{ gap: 1 }}
            />
            <Tab
              icon={<ShareIcon />}
              iconPosition="start"
              label="Share by ID"
              sx={{ gap: 1 }}
            />
            <Tab
              icon={<HistoryIcon />}
              iconPosition="start"
              label="Activity Log"
              sx={{ gap: 1 }}
            />
          </Tabs>
        </Box>

        {/* TAB 1: Partner Membership (Long-term) */}
        <TabPanel value={activeTab} index={0}>
          <Box px={3}>
            {/* Header Actions */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  External Partners
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Long-term collaborators with role-based access
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                }}
              >
                Add Partner
              </Button>
            </Box>

            {/* Partner Types */}
            <Stack direction="row" spacing={2} mb={3}>
              <Chip
                icon={<HospitalIcon />}
                label="Specialists (3)"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
              <Chip
                icon={<ScienceIcon />}
                label="Labs (1)"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
              <Chip
                icon={<PharmacyIcon />}
                label="Pharmacies (0)"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Stack>

            {/* Partners List */}
            <List sx={{ bgcolor: 'background.paper', borderRadius: 2, border: 1, borderColor: 'divider' }}>
              {mockPartners.map((partner, index) => (
                <React.Fragment key={partner.id}>
                  <ListItem
                    sx={{
                      py: 2.5,
                      '&:hover': { bgcolor: alpha('#000', 0.02) },
                      transition: 'all 0.2s',
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                          <Box
                            sx={{
                              width: 14,
                              height: 14,
                              borderRadius: '50%',
                              bgcolor: 'success.main',
                              border: '2px solid white',
                            }}
                          />
                        }
                      >
                        <Avatar
                          sx={{
                            bgcolor: 'primary.main',
                            width: 56,
                            height: 56,
                            fontSize: '1.2rem',
                            fontWeight: 700,
                          }}
                        >
                          {partner.avatar}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {partner.name}
                          </Typography>
                          <Chip
                            size="small"
                            label={partner.role}
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              bgcolor: alpha('#2563eb', 0.1),
                              color: '#2563eb',
                            }}
                          />
                          {getTypeIcon(partner.type)}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                           •  {partner.organization} • {partner.email}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={2} mt={1}>
                            <Tooltip title="Cases collaborated on">
                              <Chip
                                icon={<AssignmentIcon />}
                                label={`${partner.cases} cases`}
                                size="small"
                                variant="outlined"
                                sx={{ height: 24, fontSize: '0.75rem' }}
                              />
                            </Tooltip>
                            <Tooltip title="Last activity">
                              <Chip
                                icon={<ScheduleIcon />}
                                label={partner.lastActive}
                                size="small"
                                variant="outlined"
                                sx={{ height: 24, fontSize: '0.75rem' }}
                              />
                            </Tooltip>
                          </Box>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="View permissions">
                          <IconButton size="small">
                            <SecurityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Settings">
                          <IconButton size="small">
                            <SettingsIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < mockPartners.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>

            {/* Add Partner Card */}
            <Card
              sx={{
                mt: 3,
                border: 2,
                borderStyle: 'dashed',
                borderColor: 'primary.main',
                bgcolor: alpha('#2563eb', 0.03),
                borderRadius: 2,
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <GroupIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2, opacity: 0.6 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Add External Partner
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3} maxWidth={500} mx="auto">
                  Invite specialists, labs, pharmacies, or other healthcare providers to collaborate
                  on cases with controlled access and audit trails.
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Button variant="outlined" startIcon={<EmailIcon />} sx={{ textTransform: 'none' }}>
                    Invite by Email
                  </Button>
                  <Button variant="contained" startIcon={<PersonAddIcon />} sx={{ textTransform: 'none' }}>
                    Create Account
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </TabPanel>

        {/* TAB 2: Share by ID (Ad-hoc) */}
        <TabPanel value={activeTab} index={1}>
          <Box px={3}>
            {/* Share Generator */}
            <Card
              sx={{
                mb: 4,
                borderRadius: 3,
                bgcolor: alpha('#10b981', 0.05),
                border: 2,
                borderColor: 'success.main',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <LinkIcon sx={{ fontSize: 40, color: 'success.main' }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Generate Share Link
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Quick, lightweight collaboration - zero setup required
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Stack spacing={3}>
                  {/* Share Content */}
                  <FormControl fullWidth>
                    <InputLabel>Share Content</InputLabel>
                    <Select
                      value={shareMode}
                      onChange={(e) => setShareMode(e.target.value as any)}
                      label="Share Content"
                    >
                      <MenuItem value="image">
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <VisibilityIcon fontSize="small" />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Image Only
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Share dermatoscopic image only
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                      <MenuItem value="segmentation">
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <AssignmentIcon fontSize="small" />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Image + Segmentation
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Include segmentation analysis
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                      <MenuItem value="full">
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <SecurityIcon fontSize="small" />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Full AI Report (R0)
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Complete analysis snapshot
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {/* Access Level */}
                  <FormControl fullWidth>
                    <InputLabel>Access Level</InputLabel>
                    <Select
                      value={shareAccess}
                      onChange={(e) => setShareAccess(e.target.value as any)}
                      label="Access Level"
                    >
                      <MenuItem value="view">
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <VisibilityIcon fontSize="small" />
                          <Typography variant="body2">View Only</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="comment">
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <CommentIcon fontSize="small" />
                          <Typography variant="body2">View + Comment</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="edit">
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <EditIcon fontSize="small" />
                          <Typography variant="body2">View + Comment + Edit</Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {/* Security Options */}
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: alpha('#000', 0.03),
                      border: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      Security Options
                    </Typography>
                    <Stack spacing={2}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={requireAuth}
                            onChange={(e) => setRequireAuth(e.target.checked)}
                            color="success"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Require Authentication
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Recipient must sign in to view (recommended)
                            </Typography>
                          </Box>
                        }
                      />
                      <TextField
                        size="small"
                        label="Expiration (days)"
                        type="number"
                        defaultValue={30}
                        InputProps={{ inputProps: { min: 1, max: 365 } }}
                      />
                    </Stack>
                  </Box>

                  {/* Generate Button */}
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={<LinkIcon />}
                    onClick={generateShareLink}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.5,
                      bgcolor: 'success.main',
                      '&:hover': { bgcolor: 'success.dark' },
                    }}
                  >
                    Generate Secure Share Link
                  </Button>

                  {/* Generated Link */}
                  {generatedLink && (
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        bgcolor: alpha('#10b981', 0.1),
                        border: 2,
                        borderColor: 'success.main',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                        Share Link Generated ✓
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                          fullWidth
                          value={generatedLink}
                          size="small"
                          InputProps={{
                            readOnly: true,
                            sx: {
                              fontFamily: 'monospace',
                              fontSize: '0.85rem',
                              bgcolor: 'white',
                            },
                          }}
                        />
                        <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                          <IconButton
                            color="success"
                            onClick={handleCopyLink}
                            sx={{
                              bgcolor: 'success.main',
                              color: 'white',
                              '&:hover': { bgcolor: 'success.dark' },
                            }}
                          >
                            {copied ? <CheckIcon /> : <CopyIcon />}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Box mt={2} display="flex" gap={1.5} alignItems="center">
                        <KeyIcon fontSize="small" sx={{ color: 'success.main' }} />
                        <Typography variant="caption" color="text.secondary">
                          Share ID: <strong>SH{generatedLink.split('/').pop()}</strong> • Expires in
                          30 days • {requireAuth ? 'Auth required' : 'One-time passcode'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Active Shares */}
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Active Shares
            </Typography>
            <List sx={{ bgcolor: 'background.paper', borderRadius: 2, border: 1, borderColor: 'divider' }}>
              {mockSharedCases.map((share, index) => (
                <React.Fragment key={share.id}>
                  <ListItem sx={{ py: 2.5 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'success.main' }}>
                        <ShareIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {share.id}
                          </Typography>
                          <Chip
                            size="small"
                            label={share.status}
                            color={share.status === 'reviewed' ? 'success' : 'warning'}
                            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Shared with: <strong>{share.sharedWith}</strong> • {share.shareDate}
                          </Typography>
                          <Box display="flex" gap={1.5} mt={1}>
                            <Chip
                              size="small"
                              icon={<ScheduleIcon />}
                              label={`Expires: ${share.expiresIn}`}
                              variant="outlined"
                              sx={{ height: 24, fontSize: '0.75rem' }}
                            />
                            <Chip
                              size="small"
                              icon={<SecurityIcon />}
                              label={share.accessLevel}
                              variant="outlined"
                              sx={{ height: 24, fontSize: '0.75rem' }}
                            />
                          </Box>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton size="small" color="error">
                        <SettingsIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < mockSharedCases.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        </TabPanel>

        {/* TAB 3: Activity Log */}
        <TabPanel value={activeTab} index={2}>
          <Box px={3}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Collaboration Activity
            </Typography>
            <Alert severity="info" icon={<HistoryIcon />}>
              <Typography variant="body2">
                <strong>Audit Trail:</strong> All partner interactions are logged for compliance and
                security. HIPAA-compliant activity tracking coming soon.
              </Typography>
            </Alert>

            <Box
              sx={{
                mt: 3,
                p: 4,
                textAlign: 'center',
                bgcolor: alpha('#000', 0.02),
                borderRadius: 2,
                border: 1,
                borderStyle: 'dashed',
                borderColor: 'divider',
              }}
            >
              <HistoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Activity log will show all partner access, case shares, comments, and modifications
              </Typography>
            </Box>
          </Box>
        </TabPanel>
      </Paper>

      {/* Feature Highlights Footer */}
      <Card sx={{ mt: 4, borderRadius: 3, bgcolor: alpha('#2563eb', 0.05) }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            🚀 Key Features (Concept)
          </Typography>
          <Stack spacing={1.5}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <CheckIcon sx={{ color: 'success.main' }} />
              <Typography variant="body2">
                <strong>Org-based Tenancy:</strong> Clinic = Organization, Teams = Nurses/Doctors
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1.5}>
              <CheckIcon sx={{ color: 'success.main' }} />
              <Typography variant="body2">
                <strong>External Partners:</strong> Contributors-collaborators - scoped access to specific cases
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1.5}>
              <CheckIcon sx={{ color: 'success.main' }} />
              <Typography variant="body2">
                <strong>Share by ID:</strong> Quick links with granular permissions (image/analysis/full report)
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1.5}>
              <CheckIcon sx={{ color: 'success.main' }} />
              <Typography variant="body2">
                <strong>Audit Trail:</strong> Full history of who accessed what, when (HIPAA compliance)
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1.5}>
              <CheckIcon sx={{ color: 'success.main' }} />
              <Typography variant="body2">
                <strong>Role-Based Access:</strong> View-only, Comment, Edit permissions 
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
